import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { checkSeedanceConnection } from '../../api/seedance';
import Toast from '../../components/Toast';
import ImageLightbox from '../../components/ImageLightbox';
import VideoModal from '../../components/VideoModal';
import AudioModal from '../../components/AudioModal';
import {
  getSeedancePanelFromRoute,
  isKnownSeedancePanelRoute,
  normalizeRoutePath,
  SEEDANCE_PANEL_ROUTES,
  SEEDANCE_PANEL_TITLES,
  getTaskDetailIdFromRoute,
} from '../../router/seedanceRoutes';
import { useSeedanceAuthStore } from '../../store/seedanceAuthStore';
import { useSeedanceLibraryStore } from '../../store/seedanceLibraryStore';
import { useSeedanceUiStore } from '../../store/seedanceUiStore';
import type { Panel } from '../../store/seedanceTypes';
import {
  getLocalStorageValue,
  SEEDANCE_STORAGE_KEYS,
  setLocalStorageValue,
} from '../../utils/seedanceStorage';
import { applyIncomingSettingsFromUrl } from '../../utils/seedanceUrlSettings';

import { Sidebar } from './components/Sidebar';
import { LibraryDrawer } from './components/LibraryDrawer';
import { LibraryModals } from './components/LibraryModals';
import { LibraryPicker } from './components/LibraryPicker';
import { SeedanceModals } from './components/SeedanceModals';
import { useSeedanceTaskPolling } from '../../hooks/useSeedanceTaskPolling';
import { useSeedanceImageGenStore } from '../../store/seedanceImageGenStore';
import { useSeedanceThemeStore } from '../../store/seedanceThemeStore';
import './seedance.css';

/** 应用启动时静默 Token 校验仅执行一次（避免 StrictMode / effect 重复触发） */
let startupTokenCheckDone = false;
/** 相同 API Key 的 Token 校验并发去重 */
let tokenCheckInflight: { key: string; promise: Promise<void> } | null = null;

/**
 * Seedance 顶层布局容器。
 *
 * 职责：
 * 1. 处理路由同步与面板切换。
 * 2. 处理 API Key 初始化校验。
 * 3. 渲染全局 UI 组件（Sidebar, Drawer, Modals, Toast）。
 */
const Seedance: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const panel = useMemo(() => getSeedancePanelFromRoute(location.pathname), [location.pathname]);
  const topbarTitle = useMemo(() => {
    if (getTaskDetailIdFromRoute(location.pathname)) return '任务详情';
    return SEEDANCE_PANEL_TITLES[panel];
  }, [location.pathname, panel]);
  const theme = useSeedanceThemeStore((s) => s.theme);
  const toggleTheme = useSeedanceThemeStore((s) => s.toggleTheme);

  const { apiKey, setApiKey, setConnected } = useSeedanceAuthStore();
  const {
    setWelcomeVisible,
    welcomeKey,
    setConfigVisible,
    toast,
    modalVideoUrl,
    setModalVideoUrl,
    modalImageSrc,
    setModalImageSrc,
    modalAudioUrl,
    modalAudioTitle,
    setModalAudioUrl,
    setModalAudioTitle,
    addDebug,
    showToast,
  } = useSeedanceUiStore();

  const apiKeyRef = useRef(apiKey);
  const [envCheckMsg, setEnvCheckMsg] = useState<React.ReactNode>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    apiKeyRef.current = apiKey;
  }, [apiKey]);

  // 1. 路由规范化
  useEffect(() => {
    if (!isKnownSeedancePanelRoute(location.pathname) || normalizeRoutePath(location.pathname) === '/') {
      navigate(SEEDANCE_PANEL_ROUTES[panel], { replace: true });
    }
  }, [location.pathname, navigate, panel]);

  // 2. 环境检测
  useEffect(() => {
    const isLocalhost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.protocol === 'file:';
    setEnvCheckMsg(
      isLocalhost ? (
        <>
          <span style={{ color: 'var(--green)' }}>✅ 本地环境检测通过</span>
          <br />
          <span style={{ fontSize: 12 }}>已通过本地服务器启动，可直接调用 API，无需代理</span>
        </>
      ) : (
        <>
          <span style={{ color: 'var(--orange)' }}>⚠️ 非本地环境</span>
          <br />
          <span style={{ fontSize: 12 }}>建议通过「启动 Seedance Studio.bat」启动工具</span>
        </>
      ),
    );
  }, []);

  // 3. API Key 初始化校验（/api/usage/token/）
  const connectAPI = useCallback(
    async (silent = false, forceKey?: string) => {
      let k = (forceKey ?? apiKeyRef.current ?? '').trim();
      k = k.replace(/^Bearer\s+/i, '').replace(/[^a-zA-Z0-9-]/g, '');
      if (!k) {
        if (!silent) showToast('请输入 API Key', 'e');
        return;
      }
      setApiKey(k);
      apiKeyRef.current = k;
      setLocalStorageValue(SEEDANCE_STORAGE_KEYS.apiKey, k);

      if (tokenCheckInflight?.key === k) {
        await tokenCheckInflight.promise;
        return;
      }

      const run = (async () => {
        try {
          const tokenUsage = await checkSeedanceConnection({
            apiKey: k,
            onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError),
          });
          setConnected(true);
          if (!silent) showToast('API 连接成功 ✓', 's');
          addDebug(`Token ${tokenUsage.data?.name} 校验成功`);
        } catch (e: any) {
          setConnected(false);
          addDebug(`✕ 连接失败: ${e.message}`, true);
          if (!silent) showToast('API 连接失败: ' + e.message, 'e');
        }
      })();

      tokenCheckInflight = { key: k, promise: run };
      try {
        await run;
      } finally {
        if (tokenCheckInflight?.key === k) tokenCheckInflight = null;
      }
    },
    [addDebug, setApiKey, setConnected, showToast],
  );

  useSeedanceTaskPolling();

  useEffect(() => {
    void useSeedanceImageGenStore.getState().hydrate();
  }, []);

  useEffect(() => {
    // 外部站点跳转携带的 ?settings= 优先解析并落库，且以 URL 最新参数覆盖旧缓存
    // （apiKey / userId / safeSystemKey 在 applyIncomingSettingsFromUrl 内已无条件覆盖）
    const incoming = applyIncomingSettingsFromUrl();

    const k = incoming?.apiKey || getLocalStorageValue(SEEDANCE_STORAGE_KEYS.apiKey);
    if (!k) {
      setWelcomeVisible(true);
      return;
    }
    setApiKey(k);
    apiKeyRef.current = k;

    // URL 带来 apiKey 时：强制用最新 key 重新校验连接，保证连接状态同步到最新参数
    if (incoming?.apiKey) {
      startupTokenCheckDone = true;
      void connectAPI(true, k);
      return;
    }

    if (startupTokenCheckDone) return;
    startupTokenCheckDone = true;
    void connectAPI(true, k);
    // 仅应用启动时校验一次；勿把 connectAPI 放入 deps，避免重渲染再次请求 Token
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 4. 事件处理
  const handleWelcomeStart = () => {
    const k = welcomeKey.trim().replace(/^Bearer\s+/i, '').replace(/[^a-zA-Z0-9-]/g, '');
    if (!k) return showToast('请先填写 API Key', 'e');
    setApiKey(k);
    apiKeyRef.current = k;
    setLocalStorageValue(SEEDANCE_STORAGE_KEYS.apiKey, k);
    setWelcomeVisible(false);
    connectAPI(false, k);
  };

  const handleSaveConfig = () => {
    const token = (useSeedanceAuthStore.getState().systemToken || '').trim();
    const customGroupId = (useSeedanceAuthStore.getState().customGroupId || '').trim();
    const userId = (useSeedanceAuthStore.getState().configUserId || '').trim();
    setLocalStorageValue(SEEDANCE_STORAGE_KEYS.systemToken, token);
    setLocalStorageValue(SEEDANCE_STORAGE_KEYS.customGroupId, customGroupId);
    setLocalStorageValue(SEEDANCE_STORAGE_KEYS.configUserId, userId);
    setConfigVisible(false);
    showToast('配置已保存', 's');
  };

  const hasTaskListConfig = useCallback(() => {
    const userId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.configUserId).trim();
    return Boolean(userId);
  }, []);

  const clearLibPicker = useSeedanceLibraryStore((s) => s.clearLibPicker);

  const handleSwitchPanel = (p: Panel) => {
    if (p === 'library') clearLibPicker();
    if ((p === 'library' || p === 'tasks') && !hasTaskListConfig()) {
      return setConfigVisible(true);
    }
    navigate(SEEDANCE_PANEL_ROUTES[p]);
  };

  return (
    <div className="seedance-root" data-theme={theme}>
      <Sidebar
        panel={panel}
        onSwitchPanel={handleSwitchPanel}
        onShowWelcome={() => setWelcomeVisible(true)}
        onConnectAPI={() => connectAPI()}
      />

      <div className="main">
        <div className="topbar">
          <span className="topbar-title">{topbarTitle}</span>
          <div className="topbar-actions">
            <button
              type="button"
              className="theme-toggle-btn"
              onClick={toggleTheme}
              title={theme === 'light' ? '切换暗色模式' : '切换亮色模式'}
              aria-label={theme === 'light' ? '切换暗色模式' : '切换亮色模式'}
            >
              {theme === 'light' ? '🌙' : '☀️'}
            </button>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Outlet />
        </div>
      </div>

      <LibraryDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <button type="button" className="lib-toggle-btn" onClick={() => setDrawerOpen((v) => !v)}>
        📦
      </button>

      <LibraryModals />
      <LibraryPicker />
      <SeedanceModals
        envCheckMsg={envCheckMsg}
        onWelcomeStart={handleWelcomeStart}
        onWelcomeSkip={() => setWelcomeVisible(false)}
        onSaveConfig={handleSaveConfig}
      />

      <Toast toast={toast} />
      <VideoModal videoUrl={modalVideoUrl} onClose={() => setModalVideoUrl(null)} />
      <AudioModal
        audioUrl={modalAudioUrl}
        title={modalAudioTitle}
        onClose={() => {
          setModalAudioUrl(null);
          setModalAudioTitle(null);
        }}
      />
      <ImageLightbox imageSrc={modalImageSrc} onClose={() => setModalImageSrc(null)} />
    </div>
  );
};

export default Seedance;
